import Downshift, { ControllerStateAndHelpers } from 'downshift';
import * as React from 'react';
import { WithTestIdOptional } from 'shared-client/types/test';
import AutoSuggestBase from './AutoSuggestBase';
import AutoSuggestContext, { AutoSuggestContextValue } from './AutoSuggestContext';
import AutoSuggestData from './AutoSuggestData';
import AutoSuggestInput from './AutoSuggestInput';
import AutoSuggestMenu from './AutoSuggestMenu';
import AutoSuggestMenuItem from './AutoSuggestMenuItem';
import {
	AutoSuggestBaseProps,
	AutoSuggestItem,
	AutoSuggestMenuItemRenderProps,
	AutoSuggestRenderInputProps,
	AutoSuggestRenderMenuProps,
} from './Types';
import { assertValidAutoSuggestOptions, itemToString } from './Util';
import { ToolTipDataProps } from '../tooltip/TooltipWrapper';

export type AutoSuggestSingleProps<T extends AutoSuggestItem = AutoSuggestItem> = AutoSuggestBaseProps<T> &
	ToolTipDataProps &
	WithTestIdOptional & {
		clearInputValueAfterSelection?: boolean;
		placeholder?: string;
		forceEmptyLabel?: boolean;
		renderSingleValue?: (props: ControllerStateAndHelpers<T>) => React.ReactNode;
	};

/**
 * AutoSuggest component to select a single value
 *
 * @class AutoSuggestSingle
 * @extends {React.Component<AutoSuggestSingleProps>}
 */
class AutoSuggestSingle<T extends AutoSuggestItem = AutoSuggestItem> extends React.Component<
	AutoSuggestSingleProps<T>
> {
	public static defaultProps = {
		renderMenu: (props: AutoSuggestRenderMenuProps) => <AutoSuggestMenu {...props} />,
		renderInput: (props: AutoSuggestRenderInputProps) => (
			<AutoSuggestInput downshiftRootProps={props.downshiftRootProps} {...props} />
		),
		renderMenuItem: (props: AutoSuggestMenuItemRenderProps) => <AutoSuggestMenuItem {...props} />,
		autoSuggestOptions: {
			baseUrl: '',
			staticData: null,
		},
		clearSelectionAfterSelection: false,
		clearable: true,
	};

	/**
	 * Stores a reference to the function that will clear values from downshift. This is unfortunate but it's
	 * a result of the render props pattern making composition tricky in some situations. They are working on a
	 * hooks version but that's not currently released.
	 */
	private clearSelection: (() => void) | null = null;

	constructor(props: AutoSuggestSingleProps) {
		super(props);

		assertValidAutoSuggestOptions(props.autoSuggestOptions);
	}

	/**
	 * Callback for when a value is selected from the AutoSuggest.
	 *
	 * @memberof AutoSuggestSingle
	 */
	public handleSelect = (selectedValue: AutoSuggestItem) => {
		if (!selectedValue) {
			return;
		}

		this.props.onChange(selectedValue);

		if (this.clearSelection && this.props.clearInputValueAfterSelection) {
			this.clearSelection();
		}
	};

	public render() {
		const {
			name,
			label,
			value,
			disabled,
			onChange,
			autoSuggestOptions,
			renderMenu,
			renderInput,
			renderSingleValue,
			renderMenuItem,
			placeholder,
			forceEmptyLabel,
			clearable,
			testId,
			tooltipText,
			nameValue,
			tooltipData,
			entityDefinition,
			hasTooltipError,
		} = this.props;

		return (
			<Downshift
				itemToString={itemToString}
				onSelect={this.handleSelect}
				selectedItem={value}
				// Overriding the default behavior of getA11yStatusMessage to allow for correct implementation on the render menu level
				getA11yStatusMessage={() => ''}
			>
				{(downshift: ControllerStateAndHelpers<T>) => {
					const { isOpen, inputValue, toggleMenu, getRootProps } = downshift;
					const downshiftRootProps = getRootProps({ refKey: 'ref' }, { suppressRefError: true });

					this.clearSelection = downshift.clearSelection;

					return (
						<>
							<AutoSuggestContext.Provider
								value={
									{
										name,
										value,
										onChange,
										autoSuggestOptions,
										downshift,
										renderMenuItem,
										renderMenu,
									} as AutoSuggestContextValue
								}
							>
								{renderInput({
									label,
									labelVisible: true,
									placeholder,
									forceEmptyLabel,
									clearable,
									testId,
									disabled,
									tooltipText,
									nameValue,
									tooltipData,
									entityDefinition,
									hasTooltipError,
									downshiftRootProps: downshiftRootProps,
								})}

								{isOpen && !autoSuggestOptions.staticData ? (
									<AutoSuggestData
										query={inputValue}
										params={autoSuggestOptions.params}
										selectedItem={value ? (value as AutoSuggestItem) : null}
									>
										{(dataProps) => {
											const { createValue } = dataProps;

											const onCreateClick = async () => {
												// store new value on the server.
												const newValue = await createValue(inputValue);

												// let react-final-form that something changed.
												onChange(newValue);

												// if the menu is open, close it.
												if (isOpen) {
													toggleMenu();
												}
											};

											return (
												<AutoSuggestBase
													data={dataProps}
													onCreateClick={onCreateClick}
													renderMenu={renderMenu}
													testId={testId}
												/>
											);
										}}
									</AutoSuggestData>
								) : isOpen && autoSuggestOptions.staticData ? (
									<AutoSuggestStaticMenu
										renderMenu={renderMenu}
										staticData={autoSuggestOptions.staticData}
										inputValue={inputValue}
									/>
								) : null}
								{renderSingleValue ? renderSingleValue(downshift) : null}
							</AutoSuggestContext.Provider>
						</>
					);
				}}
			</Downshift>
		);
	}
}

interface AutoSuggestStaticMenuProps {
	renderMenu: (props: AutoSuggestRenderMenuProps) => React.ReactNode;
	staticData: AutoSuggestItem[];
	inputValue: string | null;
}

function AutoSuggestStaticMenu(props: AutoSuggestStaticMenuProps) {
	const { renderMenu, staticData, inputValue } = props;

	const filteredResults = staticData.filter(
		(autoSuggestItem) =>
			!inputValue ||
			autoSuggestItem.label.toLocaleLowerCase().includes(inputValue.trim().toLocaleLowerCase()) ||
			autoSuggestItem.value.toLocaleLowerCase().includes(inputValue.trim().toLocaleLowerCase()),
	);

	return (
		<>
			{renderMenu({
				data: {
					createValue: () => Promise.reject(),
					errorStatus: null,
					hasMore: false,
					isLoading: false,
					results: filteredResults,
				},
				onCreateClick: () => Promise.reject(),
			})}
		</>
	);
}

export default AutoSuggestSingle;
